import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DevToolSection, devFieldStack, devLabel, devMono, devResultPanel } from '/imports/ui/DevToolSection';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { TextArea } from '/imports/ui/TextArea';
import { runTestSms, getTwilioNumbers, TestSmsResult } from '/imports/api/devtools';

export const DevToolTestSms = () => {
  const [loading, setLoading] = useState(false);
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<TestSmsResult>();
  const [fromOptions, setFromOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const nums = await getTwilioNumbers.call();
        const opts: { label: string; value: string }[] = [];
        if (nums.numberLive) opts.push({ label: `Live (${nums.numberLive})`, value: nums.numberLive });
        if (nums.numberOpen) opts.push({ label: `Open (${nums.numberOpen})`, value: nums.numberOpen });
        setFromOptions(opts);
        if (opts.length) setFrom(opts[0].value);
      } 
      catch (_) { /* not authorized or no settings */ }
    })();
  }, []);

  const run = async () => {
    if (!to.trim() || !body.trim()) return toast.error('Recipient and message are required');
    setLoading(true);
    setResult(undefined);
    try {
      const res = await runTestSms.call({ to: to.trim(), from, body: body.trim() });
      setResult(res);
      if (res.ok) toast.success(`SMS sent (SID: ${res.sid})`);
      else toast.error(`SMS failed: ${res.error}`);
    } 
    catch (e: any) {
      toast.error(e.message || `${e}`);
    } 
    finally {
      setLoading(false);
    }
  };

  return (
    <DevToolSection
      title="Test SMS (Twilio)"
      description="Send a test SMS via Twilio. Select a 'from' number from the configured Twilio phone numbers."
      buttonText="Send SMS"
      onRun={run}
      loading={loading}
    >
      <div className={devFieldStack}>
        <div>
          <label className={devLabel}>From</label>
          {fromOptions.length > 0 ? (
            <Select value={from} options={fromOptions} onChange={(value) => setFrom(value)} />
          ) : (
            <Input className="block [&_input]:w-full" value={from} onChange={(value) => setFrom(value ?? '')} placeholder="From number (e.g. +18001234567)" />
          )}
        </div>
        <div>
          <label className={devLabel}>To</label>
          <Input className="block [&_input]:w-full" value={to} onChange={(value) => setTo(value ?? '')} placeholder="Recipient (e.g. +18001234567)" />
        </div>
        <div>
          <label className={devLabel}>Message</label>
          <TextArea className="min-h-16 w-full resize-y bg-page text-[13px] text-fg-1" value={body} onChange={(text) => setBody(text)} placeholder="Message body" />
        </div>
      </div>
      {result && (
        <pre className={`${devResultPanel} ${devMono} overflow-x-auto`}>
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </DevToolSection>
  );
};
